import React from 'react';
import { Popover, ListGroup } from 'react-bootstrap';

export default function TaskRecurrencePopover({ task, onUpdate, ...props }) {

    const [showCustom, setShowCustom] = React.useState(false);
    const [freq, setFreq] = React.useState('WEEKLY');
    const [interval, setInterval] = React.useState(1);
    const [weekdays, setWeekdays] = React.useState([]);
    const [byMonth, setByMonth] = React.useState(1);
    const [byMonthDay, setByMonthDay] = React.useState(1);

    React.useEffect(() => {
        if (task.recurrence_rule) {
            const parts = task.recurrence_rule.split(';');
            parts.forEach(part => {
                if (part.startsWith('FREQ=')) setFreq(part.split('=')[1]);
                if (part.startsWith('INTERVAL=')) setInterval(parseInt(part.split('=')[1]) || 1);
                if (part.startsWith('BYDAY=')) setWeekdays(part.split('=')[1].split(','));
                if (part.startsWith('BYMONTH=')) setByMonth(parseInt(part.split('=')[1]) || 1);
                if (part.startsWith('BYMONTHDAY=')) setByMonthDay(parseInt(part.split('=')[1]) || 1);
            });
        } else {
            // Reset to defaults if no rule
            setFreq('WEEKLY');
            setInterval(1);
            setWeekdays([]);
            setByMonth(new Date().getMonth() + 1); // Default to current month
            setByMonthDay(new Date().getDate());   // Default to current day
        }
    }, [task.recurrence_rule]);

    const saveCustom = () => {
        // Build RRULE manually
        const parts = [`FREQ=${freq}`];
        if (interval > 1) parts.push(`INTERVAL=${interval}`);

        if (freq === 'WEEKLY' && weekdays.length > 0) {
            parts.push(`BYDAY=${weekdays.join(',')}`);
        }

        if (freq === 'YEARLY') {
            parts.push(`BYMONTH=${byMonth}`);
        }

        if (freq === 'YEARLY' || freq === 'MONTHLY') {
            parts.push(`BYMONTHDAY=${byMonthDay}`);
        }

        onUpdate({ recurrenceRule: parts.join(';') });
        document.body.click();
    };

    const toggleWeekday = (day) => {
        if (weekdays.includes(day)) {
            setWeekdays(weekdays.filter(d => d !== day));
        } else {
            setWeekdays([...weekdays, day]);
        }
    };

    return (
        <Popover id="task-recurrence-popover" {...props} style={{ width: '300px', ...props.style }}>
            <Popover.Header className="py-2">
                <span className="fw-bold">Recurrence</span>
            </Popover.Header>
            <Popover.Body>
                <div className="mb-3">
                    <label className="small text-muted mb-1">Frequency</label>
                    <select className="form-select form-select-sm" value={freq} onChange={e => setFreq(e.target.value)}>
                        <option value="DAILY">Daily</option>
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="YEARLY">Yearly</option>
                    </select>
                </div>

                <div className="mb-3">
                    <label className="small text-muted mb-1">Repeat every</label>
                    <div className="d-flex align-items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            className="form-control form-control-sm"
                            value={interval}
                            onChange={e => setInterval(parseInt(e.target.value) || 1)}
                        />
                        <span className="small text-muted">
                            {freq === 'DAILY' ? 'days' : freq === 'WEEKLY' ? 'weeks' : freq === 'MONTHLY' ? 'months' : 'years'}
                        </span>
                    </div>
                </div>

                {freq === 'WEEKLY' && (
                    <div className="mb-3">
                        <label className="small text-muted mb-1">On days</label>
                        <div className="d-flex justify-content-between">
                            {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map(day => (
                                <div
                                    key={day}
                                    className={`rounded-circle d-flex align-items-center justify-content-center border cursor-pointer ${weekdays.includes(day) ? 'bg-primary text-white border-primary' : 'bg-white text-muted border'}`}
                                    style={{ width: 28, height: 28, fontSize: '0.65rem' }}
                                    onClick={() => toggleWeekday(day)}
                                >
                                    {day.substring(0, 1)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {freq === 'MONTHLY' && (
                    <div className="mb-3">
                        <label className="small text-muted mb-1">On day</label>
                        <select
                            className="form-select form-select-sm"
                            value={byMonthDay}
                            onChange={e => setByMonthDay(parseInt(e.target.value))}
                        >
                            <option value="-1">Last Day</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                )}

                {freq === 'YEARLY' && (
                    <div className="mb-3">
                        <label className="small text-muted mb-1">Date</label>
                        <div className="d-flex flex-column gap-2">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={(function () {
                                    // Construct date string YYYY-MM-DD for display
                                    const year = new Date().getFullYear();
                                    const month = (byMonth || 1) - 1;
                                    let day = byMonthDay;

                                    if (day === -1) {
                                        // If set to last day, show valid last day of that month
                                        day = new Date(year, month + 1, 0).getDate();
                                    } else {
                                        // Ensure day is valid for the month
                                        const maxDay = new Date(year, month + 1, 0).getDate();
                                        if (day > maxDay) day = maxDay;
                                    }

                                    // Format manually as YYYY-MM-DD to avoid timezone issues
                                    const mStr = String(month + 1).padStart(2, '0');
                                    const dStr = String(day).padStart(2, '0');
                                    return `${year}-${mStr}-${dStr}`;
                                })()}
                                onChange={(e) => {
                                    if (!e.target.value) return;
                                    // Parse YYYY-MM-DD straight from input
                                    const [y, m, d] = e.target.value.split('-').map(Number);
                                    setByMonth(m);
                                    if (byMonthDay !== -1) {
                                        setByMonthDay(d);
                                    }
                                }}
                            />

                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="lastDaySwitch"
                                    checked={byMonthDay === -1}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setByMonthDay(-1);
                                        } else {
                                            // Revert to today's day (clamped later if needed) or 1
                                            setByMonthDay(new Date().getDate());
                                        }
                                    }}
                                />
                                <label className="form-check-label small text-muted" htmlFor="lastDaySwitch">
                                    Last day of the month
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                <div className="d-flex gap-2 flex-column">
                    <button className="btn btn-primary btn-sm w-100" onClick={saveCustom}>Save Recurrence</button>
                    {(task.recurrence_rule) && (
                        <button className="btn btn-outline-danger btn-sm w-100" onClick={() => { onUpdate({ recurrenceRule: "" }); document.body.click(); }}>Remove Recurrence</button>
                    )}
                </div>
            </Popover.Body>
        </Popover>
    );
}
